import { AuthUserDTO, ServiceScope } from '../types/user-profile.types';

/**
 * Utility class for checking service-based permissions from JWT payload
 * These permissions are extracted from the JWT access token and stored in the user object
 */
export class ServicePermissionUtils {
  /**
   * Check if user has access to a specific service
   * @param user - The authenticated user object
   * @param serviceKey - Service identifier (e.g., 'admin', 'expert', 'hr')
   * @returns true if user has any role for the service
   */
  static hasServiceAccess(
    user: AuthUserDTO | null,
    serviceKey: string
  ): boolean {
    if (!user || !user.svc_roles) {
      return false;
    }
    return !!user.svc_roles[serviceKey]?.length;
  }

  /**
   * Check if user has specific permission in a service
   * @param user - The authenticated user object
   * @param serviceKey - Service identifier (e.g., 'admin', 'expert')
   * @param permission - Permission string (e.g., 'dashboard:read', 'users:write')
   * @returns true if user has the specified permission
   */
  static hasPermission(
    user: AuthUserDTO | null,
    serviceKey: string,
    permission: string
  ): boolean {
    if (!user || !user.svc_perms) {
      return false;
    }
    return user.svc_perms[serviceKey]?.includes(permission) ?? false;
  }

  /**
   * Check if user has any of the specified permissions in a service
   * @param user - The authenticated user object
   * @param serviceKey - Service identifier
   * @param permissions - Array of permission strings
   * @returns true if user has at least one of the permissions
   */
  static hasAnyPermission(
    user: AuthUserDTO | null,
    serviceKey: string,
    permissions: string[]
  ): boolean {
    return permissions.some((perm) =>
      this.hasPermission(user, serviceKey, perm)
    );
  }

  /**
   * Check if user has all of the specified permissions in a service
   * @param user - The authenticated user object
   * @param serviceKey - Service identifier
   * @param permissions - Array of permission strings
   * @returns true if user has all of the permissions
   */
  static hasAllPermissions(
    user: AuthUserDTO | null,
    serviceKey: string,
    permissions: string[]
  ): boolean {
    return permissions.every((perm) =>
      this.hasPermission(user, serviceKey, perm)
    );
  }

  /**
   * Check if user has specific role in a service
   * @param user - The authenticated user object
   * @param serviceKey - Service identifier
   * @param role - Role name (e.g., 'admin', 'manager', 'Expert Lead')
   * @returns true if user has the specified role
   */
  static hasServiceRole(
    user: AuthUserDTO | null,
    serviceKey: string,
    role: string
  ): boolean {
    if (!user || !user.svc_roles) {
      return false;
    }
    return user.svc_roles[serviceKey]?.includes(role) ?? false;
  }

  /**
   * Check if user has any of the specified roles in a service
   * @param user - The authenticated user object
   * @param serviceKey - Service identifier
   * @param roles - Array of role names
   * @returns true if user has at least one of the roles
   */
  static hasAnyServiceRole(
    user: AuthUserDTO | null,
    serviceKey: string,
    roles: string[]
  ): boolean {
    return roles.some((role) => this.hasServiceRole(user, serviceKey, role));
  }

  /**
   * Get list of services user has access to
   * @param user - The authenticated user object
   * @returns Array of service keys user has access to
   */
  static getAccessibleServices(user: AuthUserDTO | null): string[] {
    if (!user || !user.svc_roles) {
      return [];
    }
    return Object.keys(user.svc_roles).filter(
      (service) => user.svc_roles![service]?.length > 0
    );
  }

  /**
   * Get all roles for a specific service
   * @param user - The authenticated user object
   * @param serviceKey - Service identifier
   * @returns Array of role names for the service
   */
  static getServiceRoles(
    user: AuthUserDTO | null,
    serviceKey: string
  ): string[] {
    if (!user || !user.svc_roles) {
      return [];
    }
    return user.svc_roles[serviceKey] ?? [];
  }

  /**
   * Get all permissions for a specific service
   * @param user - The authenticated user object
   * @param serviceKey - Service identifier
   * @returns Array of permission strings for the service
   */
  static getServicePermissions(
    user: AuthUserDTO | null,
    serviceKey: string
  ): string[] {
    if (!user || !user.svc_perms) {
      return [];
    }
    return user.svc_perms[serviceKey] ?? [];
  }

  /**
   * Get scope information for a specific service
   * @param user - The authenticated user object
   * @param serviceKey - Service identifier
   * @returns Array of scope objects (company/branch access)
   */
  static getServiceScopes(
    user: AuthUserDTO | null,
    serviceKey: string
  ): ServiceScope[] {
    if (!user || !user.svc_scopes) {
      return [];
    }
    return user.svc_scopes[serviceKey] ?? [];
  }

  /**
   * Check if user has access to all branches in a service
   * @param user - The authenticated user object
   * @param serviceKey - Service identifier
   * @returns true if user has all_branches access
   */
  static hasAllBranchesAccess(
    user: AuthUserDTO | null,
    serviceKey: string
  ): boolean {
    const scopes = this.getServiceScopes(user, serviceKey);
    return scopes.some((scope) => scope.all_branches === true);
  }

  /**
   * Check if user has access to a specific branch in a service
   * @param user - The authenticated user object
   * @param serviceKey - Service identifier
   * @param branchId - Branch identifier
   * @returns true if user has access to the branch
   */
  static hasBranchAccess(
    user: AuthUserDTO | null,
    serviceKey: string,
    branchId: string
  ): boolean {
    const scopes = this.getServiceScopes(user, serviceKey);
    return scopes.some(
      (scope) =>
        scope.all_branches === true ||
        scope.branch_ids?.includes(branchId) === true
    );
  }

  /**
   * Get all branch IDs user has access to in a service
   * @param user - The authenticated user object
   * @param serviceKey - Service identifier
   * @returns Array of branch IDs
   */
  static getAccessibleBranchIds(
    user: AuthUserDTO | null,
    serviceKey: string
  ): string[] {
    const scopes = this.getServiceScopes(user, serviceKey);
    const branchIds: string[] = [];

    scopes.forEach((scope) => {
      if (scope.branch_ids) {
        branchIds.push(...scope.branch_ids);
      }
    });

    return [...new Set(branchIds)]; // Remove duplicates
  }
}
